import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { MarketingSEO } from '@/components/seo/MarketingSEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { smartAlertPageMap, smartAlertPages } from '@/data/smartAlertPages';
import { getKeywordsForPath } from '@/data/seoKeywordMap';
import { SEO_ROUTES } from '@/seo/seo-routes';
import { Link } from 'react-router-dom';

const monitoringSignals = [
  'Repeated insults, mockery, or threats from the same contact over several days',
  'Group chats where multiple kids "dog pile" with the same isolating language',
  'Escalating tone shifts that move from teasing to intimidation or exclusion',
  'Late-night or rapid-fire messages demanding responses from your child',
  'Screenshots, photos, or inside jokes used to embarrass or control your child'
];

const alertScreenshotExamples = [
  {
    title: 'Example: group chat pile-on',
    description:
      'Joey highlights when several contacts target your child in the same conversation so you can screenshot the context and intervene.'
  },
  {
    title: 'Example: language escalating to threats',
    description: 'See when "jokes" turn into intimidation so you know when to loop in school leadership or another adult.'
  },
  {
    title: 'Example: isolating DMs after a block',
    description:
      'Persistent DMs after your child leaves the group are flagged so you can document the behavior and set new boundaries.'
  }
];

const realWorldScenarios = [
  {
    title: 'The group chat pile-on',
    description:
      'A class group chat starts with light teasing, then suddenly half the kids post memes aimed at your child. Joey detects that multiple people are using the same insults so you can save evidence and address the behavior quickly.'
  },
  {
    title: 'The relentless nickname',
    description:
      'One friend keeps using a cruel nickname, promising "it\'s just a joke." When your child stops replying, the friend sends 20 rapid-fire messages demanding a response. The alert surfaces the pattern so you can coach them through a firm boundary.'
  },
  {
    title: 'The sudden freeze-out',
    description:
      'A teammate convinces others to cut your child out of the chat, then keeps DMing screenshots to rub it in. Joey flags the coordinated exclusion and ongoing harassment so you know it\'s more than a minor disagreement.'
  }
];

const smartAlertCategoryLinks = smartAlertPages
  .filter((category) => category.slug !== 'bullying-harassment')
  .map(({ slug, path, title, cardDescription }) => ({
    slug,
    path,
    title,
    description: cardDescription ?? ''
  }));

const SmartAlertBullyingHarassment = () => {
  const page = smartAlertPageMap['bullying-harassment'];

  if (!page) {
    throw new Error('Smart alert data missing for bullying & harassment');
  }

  const keywords = getKeywordsForPath(page.path);
  const canonicalUrl = SEO_ROUTES.smartAlertsBullyingHarassment;

  return (
    <>
      <MarketingSEO
        title={`${page.title} Smart Alert | Joey`}
        description={page.seoDescription}
        url={canonicalUrl}
        keywords={keywords}
        image="/img/og/open_graph.png"
      />

      <Navigation />

      <main className="bg-gradient-to-b from-white via-slate-50 to-white">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_rgba(245,158,11,0.2),_transparent_55%)]" />
          <div className="relative max-w-6xl mx-auto px-4 pt-36 pb-24 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr),380px] items-start">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${page.accentColor}`}>
                    <img src={page.icon.src} alt={page.icon.alt} className="w-9 h-9" loading="lazy" />
                  </div>
                  <span className={`text-sm font-semibold uppercase tracking-[0.3em] ${page.accentText}`}>
                    Smart alert category
                  </span>
                </div>
                <div className="space-y-4">
                  <h1 className="text-4xl md:text-5xl font-semibold text-brand-navy">{page.title}</h1>
                  <p className="text-2xl text-slate-800">Stand up to repeated put-downs before they sink in.</p>
                </div>
                <p className="text-slate-700 leading-relaxed mt-6 text-lg max-w-2xl">
                  Joey spots intimidation, coordinated pile-ons, and isolating language so you can act before a child's confidence wears down. Instead
                  of combing through every chat, you receive clear signals when someone is crossing the line from teasing to harassment, giving you time to coach, document, and involve other adults if needed.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg">
                    <Link to="/get-started">Get Joey Smart Alerts</Link>
                  </Button>
                  <p className="text-sm uppercase tracking-[0.3em] text-slate-500">Signals, not full transcripts</p>
                </div>
              </div>

              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-brand-navy mb-5">What this alert monitors</h2>
                  <ul className="space-y-4 text-slate-700">
                    {monitoringSignals.map((signal) => (
                      <li key={signal} className="flex gap-3">
                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-brand-yellow" />
                        <span>{signal}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                    Joey surfaces patterns so you can respond with empathy and documentation, not guesswork.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why this alert matters */}
        <section className="py-20 px-4 bg-white border-t border-slate-100">
          <div className="max-w-5xl mx-auto grid gap-12 lg:grid-cols-[minmax(0,1.2fr),minmax(0,0.8fr)] items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Why this alert matters</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-2">Why bullying & harassment alerts matter</h2>
              <div className="space-y-5 text-slate-700 leading-relaxed text-lg mt-8">
                <p>
                  Kids move fast between inside jokes, sarcasm, and real hostility. Without context, it is hard to know when to step in or when a disagreement has already become harmful. Joey distinguishes repeated, targeted language from one-off spats so you can see the trend line.
                </p>
                <p>
                  Early visibility lets families check in with calm questions, document proof for schools, and set boundaries before things escalate into threats or social isolation.
                </p>
              </div>
            </div>
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-6 space-y-5">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-500">Parent takeaways</p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-yellow" />
                    <span>Know when playful teasing has turned mean without reading every message.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-navy" />
                    <span>Gather context you can share with school leadership or another caregiver.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-teal" />
                    <span>Coach your child through scripts, coping strategies, or next steps.</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What the alert looks like */}
        <section className="py-20 px-4 bg-slate-900 text-white">
          <div className="max-w-5xl mx-auto space-y-10">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-white/60">In the Joey dashboard</p>
              <h2 className="text-3xl md:text-4xl font-semibold mt-3">What this alert looks like in Joey</h2>
              <p className="text-lg text-white/90 mt-4">
                When Joey detects ongoing intimidation, you'll see a Smart Alert summarizing who is driving it and how often. A typical card might read:
                "Group chat 'Year 7 Squad' shows 4 contacts sending repeated insults to [Child's name] over the last 5 days. Check in and decide whether to save evidence."
              </p>
              <div className="mt-6 space-y-3 text-white/80">
                <p>Each alert includes:</p>
                <ul className="space-y-2">
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-yellow" />
                    <span>The contact names or phone numbers involved</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-yellow" />
                    <span>A short description of the pattern Joey spotted</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-2 w-2 rounded-full bg-brand-yellow" />
                    <span>The timeframe (for example, "over the last 7 days")</span>
                  </li>
                </ul>
                <p>You can open the alert for additional context or simply use it as your cue to start a conversation.</p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {alertScreenshotExamples.map((example) => (
                <Card key={example.title} className="bg-slate-800/70 border border-white/10">
                  <CardContent className="p-5 space-y-4">
                    <div className="h-48 rounded-xl border border-dashed border-white/30 bg-slate-900/50 flex items-center justify-center text-white/50 text-sm text-center px-4">
                      Screenshot placeholder
                    </div>
                    <div>
                      <p className="font-semibold">{example.title}</p>
                      <p className="text-sm text-white/80 mt-2 leading-relaxed">{example.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Real world scenarios */}
        <section className="py-20 px-4 bg-white border-t border-slate-100">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">Real-world examples</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy mt-2">What this might look like in real life</h2>
              <p className="text-lg text-slate-600 mt-4">
                Each scenario is different, but the goal is the same: give your child backup, document what happened, and make a plan they can trust.
              </p>
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
              {realWorldScenarios.map((scenario) => (
                <Card key={scenario.title} className="h-full border-slate-200 shadow-sm">
                  <CardContent className="p-6 flex flex-col">
                    <span className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-400">Scenario</span>
                    <h3 className="text-xl font-semibold text-brand-navy mt-3">{scenario.title}</h3>
                    <p className="text-slate-700 leading-relaxed mt-4">{scenario.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* See all categories */}
        <section className="py-20 px-4 bg-slate-50 border-t border-slate-100">
          <div className="max-w-6xl mx-auto space-y-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div className="max-w-3xl space-y-4">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500">See all smart alert categories</p>
                <h2 className="text-3xl md:text-4xl font-semibold text-brand-navy">See all Smart Alert categories</h2>
                <p className="text-slate-700 text-lg">
                  Each Smart Alert focuses on a different type of risk so you can respond with confidence, not guesswork.
                </p>
              </div>
              <Button asChild size="lg" className="shrink-0">
                <Link to="/features/smart-alerts">Return to Smart Alerts</Link>
              </Button>
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              {smartAlertCategoryLinks.map((category) => (
                <Card key={category.slug} className="border-slate-200 bg-white hover:border-brand-cyan transition">
                  <CardContent className="p-6 flex flex-col gap-4">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-slate-400">Smart alert</p>
                      <h3 className="text-2xl font-semibold text-brand-navy mt-2">{category.title}</h3>
                    </div>
                    <p className="text-slate-600 flex-1">{category.description}</p>
                    <Button asChild variant="ghost" className="justify-start px-0 text-brand-navy hover:text-brand-cyan">
                      <Link to={category.path} aria-label={`Read about the ${category.title} Smart Alert`}>
                        Explore alert
                        <span className="ml-2" aria-hidden>
                          {'->'}
                        </span>
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default SmartAlertBullyingHarassment;
